import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import type { TenantRole, GlobalRole } from '@detect/shared';
import { getFirebaseAuth } from '../config/firebase';
import { api } from '../services/api';
import { mergeDelegatedTenants, type DelegatedTenantDto } from './mergeTenants';

/**
 * Auth context (frontend-patterns §3, adapted): Firebase sign-in against
 * the shared identity project, then a /auth/me API bootstrap — Detect's
 * Firestore rules are deny-all, so identity NEVER comes from client
 * Firestore reads. `tokenReady` gates every API call (§3.2).
 */

export interface TenantSummary {
  tenantId: string;
  name: string;
  status: string;
  role: TenantRole;
  clientIds: string[] | null;
  /** Set when this tenant is reached through a management grant (#16), not a membership. */
  delegated?: { viaTenantId: string };
}

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  globalRole: GlobalRole;
  authSource: string;
  tenants: TenantSummary[];
}

interface AuthContextType {
  user: AuthUser | null;
  firebaseUser: FirebaseUser | null;
  /** Initial auth state still resolving — render AppInitializing. */
  loading: boolean;
  /** ID token usable for API calls (§3.2) — gate every fetch on this. */
  tokenReady: boolean;
  /** /auth/me failed after sign-in (e.g. no Detect account). */
  bootstrapError: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  /** Federated sign-in (popup) — tech-arch sign-in surface: Google +
   *  Microsoft alongside email/password. */
  signInWithProvider: (provider: 'google' | 'microsoft') => Promise<void>;
  signOut: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenReady, setTokenReady] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  // Monotonic auth-state sequence: a slow /auth/me from a PREVIOUS
  // sign-in must never overwrite the state of the current one (rapid
  // account switching race).
  const authSeq = useRef(0);

  const bootstrap = useCallback(async () => {
    const seq = authSeq.current;
    try {
      const me = await api.get<AuthUser & { delegatedTenants?: DelegatedTenantDto[] }>('/auth/me');
      if (authSeq.current !== seq) return; // stale resolution — drop
      const { delegatedTenants, ...rest } = me;
      setUser({ ...rest, tenants: mergeDelegatedTenants(rest.tenants, delegatedTenants) });
      setBootstrapError(null);
    } catch (error) {
      if (authSeq.current !== seq) return;
      setUser(null);
      setBootstrapError(
        error instanceof Error ? error.message : 'Failed to load your account'
      );
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getFirebaseAuth(), async (fbUser) => {
      authSeq.current += 1;
      const seq = authSeq.current;
      setFirebaseUser(fbUser);
      if (fbUser) {
        // Force a fresh token BEFORE flagging ready — the /auth/me call
        // below implicitly waits for it via getIdToken().
        await fbUser.getIdToken();
        if (authSeq.current !== seq) return;
        setTokenReady(true);
        await bootstrap();
      } else {
        setUser(null);
        setTokenReady(false);
        setBootstrapError(null);
      }
      if (authSeq.current === seq) setLoading(false);
    });
    return () => unsubscribe();
  }, [bootstrap]);

  const signIn = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
  }, []);

  const signInWithProvider = useCallback(
    async (kind: 'google' | 'microsoft') => {
      // Desk's provider setup: account chooser for Google; Microsoft is
      // the multi-tenant `microsoft.com` OAuthProvider (any Entra org).
      const provider =
        kind === 'google'
          ? (() => {
              const p = new GoogleAuthProvider();
              p.setCustomParameters({ prompt: 'select_account' });
              return p;
            })()
          : new OAuthProvider('microsoft.com');
      await signInWithPopup(getFirebaseAuth(), provider);
    },
    []
  );

  const signOut = useCallback(async () => {
    await firebaseSignOut(getFirebaseAuth());
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        loading,
        tokenReady,
        bootstrapError,
        signIn,
        signInWithProvider,
        signOut,
        refreshMe: bootstrap,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
